import { Link, useLocation } from 'react-router';
import cs from 'classnames';
import { useNavigationsQuery } from '@data/navigations/useNavigationsQuery';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { DeviceType } from '@repo/types';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { trackClick } from '@ui/utils/tracking';
import { SocialNetworks } from '@ui/league/SocialNetworks/SocialNetworks';

type NavigationProps = {
  className?: string;
};

type ParsedUrl = {
  path: string;
  query?: string;
  hash?: string;
};

// Helper function to parse URL into components
const parseUrl = (url: string): ParsedUrl => {
  // Handle URLs that are just a hash (e.g., #midlands)
  if (url.startsWith('#')) {
    return { path: '', query: undefined, hash: url };
  }

  const [pathWithQuery = '', hash] = url.split('#');
  const [path = '', query] = pathWithQuery.split('?');
  // Ensure hash includes the # prefix if it exists
  return { path, query, hash: hash ? `#${hash}` : undefined };
};

// Check if URL is external
const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http') || (url.includes('.') && !url.startsWith('/'));
};

// Normalize path by ensuring it starts with /
const normalizePath = (path: string): string => {
  if (!path) return '';
  return path.startsWith('/') ? path : `/${path}`;
};

// Check if two URLs match based on their components
const urlsMatch = (
  navUrl: ParsedUrl,
  currentUrl: ParsedUrl,
  navUrlString: string,
  currentUrlString: string
): boolean => {
  // Normalize paths for comparison
  const normalizedNavPath = normalizePath(navUrl.path);
  const normalizedCurrentPath = normalizePath(currentUrl.path);

  // Exact match (full string comparison) - also try with normalized paths
  if (navUrlString === currentUrlString) {
    return true;
  }

  // Match with hash (e.g., /club-overview#premier)
  // Hashes are already normalized with # prefix in parseUrl
  if (navUrl.hash && currentUrl.hash) {
    // If nav item is just a hash (e.g., #midlands), match any path with that hash
    if (!navUrl.path && navUrl.hash === currentUrl.hash) {
      return true;
    }
    // If both have paths, they must match along with the hash
    if (normalizedNavPath === normalizedCurrentPath && navUrl.hash === currentUrl.hash) {
      return true;
    }
  }

  // Also check path match without hash if nav doesn't have hash but current does
  if (!navUrl.hash && !navUrl.query && currentUrl.path) {
    if (normalizedNavPath === normalizedCurrentPath) {
      return true;
    }
  }

  return false;
};

// Check if a plain path should be active (fallback when no exact match exists)
const isPathActiveAsFallback = (
  navUrl: ParsedUrl,
  _currentUrl: ParsedUrl,
  currentPath: string,
  hasExactMatch: boolean
): boolean => {
  // Only match plain paths when nav item has no query/hash and no exact match exists
  if (!navUrl.query && !navUrl.hash && !hasExactMatch) {
    const normalizedNavPath = normalizePath(navUrl.path);
    const normalizedCurrentPath = normalizePath(currentPath);

    // Exact path match
    if (normalizedCurrentPath === normalizedNavPath) {
      return true;
    }
    // Nested route match
    if (normalizedNavPath !== '/' && normalizedCurrentPath.startsWith(normalizedNavPath + '/')) {
      return true;
    }
  }
  return false;
};

function Nav({ className }: NavigationProps) {
  // All hooks must come first, before any conditional logic
  const { data, isLoading, error } = useNavigationsQuery();
  const deviceType = useDeviceType();
  const location = useLocation();
  const navListRef = useRef<HTMLUListElement>(null);

  // State for mobile menu toggle
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // State for accordion expansion in mobile menu
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Track hash changes manually since React Router doesn't always update location.hash
  const [currentHash, setCurrentHash] = useState(() => window.location.hash);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };

    window.addEventListener('hashchange', handleHashChange);

    // Also update when location changes
    setCurrentHash(window.location.hash);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location]);

  const isMobile = useMemo(() => deviceType === DeviceType.mobile || deviceType === DeviceType.tablet, [deviceType]);

  const headerNavigation = data?.header;
  const navigationNodes = useMemo(
    () => (isMobile ? headerNavigation?.mobileNodes : headerNavigation?.desktopNodes),
    [isMobile, headerNavigation]
  );

  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => {
    trackClick(isMobileMenuOpen ? 'closeNavBurgerMenu' : 'openNavBurgerMenu');
    setIsMobileMenuOpen((prev) => !prev);
  }, [isMobileMenuOpen]);

  // Close menu when clicking a link
  const handleLinkClick = useCallback(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  // Toggle accordion item expansion
  const toggleAccordion = useCallback((itemLabel: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemLabel)) {
        newSet.delete(itemLabel);
      } else {
        newSet.add(itemLabel);
      }
      return newSet;
    });
  }, []);

  // Close menu when location changes
  useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
      setExpandedItems(new Set()); // Reset accordion state
    }
  }, [location.pathname, isMobile]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isMobileMenuOpen]);

  // Memoize the navigation items to prevent unnecessary re-renders
  const navItems = useMemo(() => {
    if (!navigationNodes || navigationNodes.length === 0) {
      return [];
    }

    // Collect all navigation URLs for exact match checking
    const allNavUrls: string[] = [];
    navigationNodes.forEach((item) => {
      if (item.url && !isExternalUrl(item.url)) {
        allNavUrls.push(item.url);
      }
      item.children?.forEach((child) => {
        if (child.url && !isExternalUrl(child.url)) {
          allNavUrls.push(child.url);
        }
      });
    });

    // Current location - use tracked hash instead of location.hash
    const currentFullPath = location.pathname + location.search + currentHash;
    const currentParsed = parseUrl(currentFullPath);

    // Check if there's an exact match in the navigation
    const hasExactMatch = allNavUrls.some((url) => {
      const urlParsed = parseUrl(url);
      return urlsMatch(urlParsed, currentParsed, url, currentFullPath);
    });

    // Helper to determine if a nav item is active
    const checkItemActive = (url: string | null | undefined): boolean => {
      if (!url || isExternalUrl(url)) return false;

      const itemParsed = parseUrl(url);

      // Check exact or hash match
      if (urlsMatch(itemParsed, currentParsed, url, currentFullPath)) {
        return true;
      }

      // Special case: If current URL has a hash but nav item is just the path
      // Mark the path as active (e.g., /club-overview is active when on /club-overview#midlands)
      if (currentParsed.hash && !itemParsed.hash && !itemParsed.query) {
        const normalizedItemPath = normalizePath(itemParsed.path);
        const normalizedCurrentPath = normalizePath(currentParsed.path);

        if (normalizedItemPath === normalizedCurrentPath) {
          // Only if there's no more specific match with the hash
          const hasSpecificHashMatch = allNavUrls.some((navUrl) => {
            const parsed = parseUrl(navUrl);
            const normalizedNavPath = normalizePath(parsed.path);
            return normalizedNavPath === normalizedCurrentPath && parsed.hash === currentParsed.hash;
          });
          if (!hasSpecificHashMatch) {
            return true;
          }
        }
      }

      // Check fallback path match
      return isPathActiveAsFallback(itemParsed, currentParsed, location.pathname, hasExactMatch);
    };

    // Calculate match scores for each navigation item
    const itemsWithScores = navigationNodes.map((item) => {
      let bestScore = 0;
      let childIsActive = false;
      let activeChild = null;

      // Check children first for exact matches
      if (item.children) {
        for (const child of item.children) {
          if (checkItemActive(child.url)) {
            childIsActive = true;
            activeChild = child;
            bestScore = 100; // Child match gets highest priority
            break;
          }
        }
      }

      // Check the item itself if no child was active
      if (!childIsActive && item.url && !isExternalUrl(item.url)) {
        const itemParsed = parseUrl(item.url);
        const normalizedItemPath = normalizePath(itemParsed.path);
        const normalizedCurrentPath = normalizePath(currentParsed.path);

        // Exact full match (path + query + hash)
        if (item.url === currentFullPath) {
          bestScore = 90;
        }
        // Path + query match (for URLs like /news?tags=podcasts)
        else if (
          itemParsed.query &&
          currentParsed.query &&
          normalizedItemPath === normalizedCurrentPath &&
          itemParsed.query === currentParsed.query
        ) {
          bestScore = 80;
        }
        // Path + hash match
        else if (
          itemParsed.hash &&
          currentParsed.hash &&
          normalizedItemPath === normalizedCurrentPath &&
          itemParsed.hash === currentParsed.hash
        ) {
          bestScore = 70;
        }
        // Path only match
        else if (!itemParsed.query && !itemParsed.hash && normalizedItemPath === normalizedCurrentPath) {
          bestScore = 50;
        }
        // Partial path match (nested routes)
        else if (
          !itemParsed.query &&
          !itemParsed.hash &&
          normalizedItemPath !== '/' &&
          normalizedCurrentPath.startsWith(normalizedItemPath + '/')
        ) {
          bestScore = 30;
        }
      }

      return {
        item,
        score: bestScore,
        childIsActive,
        activeChild,
      };
    });

    // Find the best matching root item
    const bestRootMatch = itemsWithScores.reduce<{
      item: (typeof navigationNodes)[0] | null;
      score: number;
      childIsActive: boolean;
      activeChild: unknown;
    }>(
      (best, current) => {
        if (current.score > best.score) {
          return current;
        }
        return best;
      },
      { item: null, score: 0, childIsActive: false, activeChild: null }
    );

    // Process navigation items with the scoring results
    return navigationNodes.map((item) => {
      const scoreItem = itemsWithScores.find((si) => si.item === item);
      const isTheBestMatch = bestRootMatch.item === item && bestRootMatch.score > 0;

      return {
        ...item,
        isActive: isTheBestMatch,
        children: item.children?.map((child) => ({
          ...child,
          isActive: isTheBestMatch && scoreItem?.activeChild === child,
        })),
      };
    });
  }, [navigationNodes, location.pathname, location.search, currentHash]);

  // Early returns come after all hooks
  if (isLoading) {
    return null;
  }

  if (error || !navigationNodes || navigationNodes.length === 0) {
    return null;
  }

  if (isMobile) {
    return (
      <nav className={cs('nav', className, 'nav__mobile')}>
        <ul className={cs('nav__list', {})} ref={navListRef}>
          {navItems.slice(0, 3).map((item) => (
            <li className={cs('nav__list-item', { 'active-parent': item.isActive })} key={item.label}>
              {item.url ? (
                <Link
                  className={cs('nav__link standalone', { active: item.isActive })}
                  to={item.url.includes('.') && !item.url.startsWith('http') ? `https://${item.url}` : item.url}
                  target={item.sameTab ? '_self' : '_blank'}
                  onClick={handleLinkClick}
                >
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
              {item.children && item.children.length > 0 && (
                <ul>
                  {item.children.map((child) => (
                    <li key={child.id}>
                      {child.url ? (
                        <Link
                          className={cs('nav__link standalone', { active: child.isActive })}
                          to={
                            child.url.includes('.') && !child.url.startsWith('http')
                              ? `https://${child.url}`
                              : child.url
                          }
                          target={item.sameTab ? '_self' : '_blank'}
                          onClick={handleLinkClick}
                        >
                          {child.label}
                        </Link>
                      ) : (
                        child.label
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        {/* Navigation menu */}
        <div
          className={cs('nav__mobile-menu', {
            'nav__mobile-menu--open': isMobileMenuOpen,
          })}
        >
          <div className="nav__mobile-menu-content">
            <ul
              className={cs('nav__list', {
                'nav__list--mobile': true,
              })}
              ref={navListRef}
            >
              {navItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.label);

                return (
                  <li className={cs('nav__list-item', { 'active-parent': item.isActive })} key={item.label}>
                    {hasChildren ? (
                      // Item with children - render as accordion
                      <>
                        <div
                          className="nav__accordion-header"
                          onClick={(e) => toggleAccordion(item.label, e)}
                          aria-expanded={isExpanded}
                          aria-label={`Toggle ${item.label} submenu`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleAccordion(item.label, e as any);
                            }
                          }}
                        >
                          <span className={cs('nav__link standalone', { active: item.isActive })}>{item.label}</span>
                          <span
                            className={cs('nav__accordion-toggle', { 'nav__accordion-toggle--expanded': isExpanded })}
                          >
                            <svg
                              className="nav__accordion-icon"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                        {isExpanded && item.children && (
                          <ul className="nav__accordion-content">
                            {item.children.map((child) => (
                              <li key={child.id}>
                                {child.url ? (
                                  <Link
                                    className={cs('nav__link standalone', { active: child.isActive })}
                                    to={
                                      child.url.includes('.') && !child.url.startsWith('http')
                                        ? `https://${child.url}`
                                        : child.url
                                    }
                                    target={item.sameTab ? '_self' : '_blank'}
                                    onClick={handleLinkClick}
                                  >
                                    {child.label}
                                  </Link>
                                ) : (
                                  child.label
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : // Item without children - render normally
                    item.url ? (
                      <Link
                        className={cs('nav__link standalone', { active: item.isActive })}
                        to={item.url.includes('.') && !item.url.startsWith('http') ? `https://${item.url}` : item.url}
                        target={item.sameTab ? '_self' : '_blank'}
                        onClick={handleLinkClick}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      item.label
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="nav__mobile-menu-footer">
            <SocialNetworks className="nav__social-networks" />
          </div>
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && <div className="nav__overlay" onClick={toggleMobileMenu} aria-hidden="true" />}
        {/* Burger menu button for mobile */}
        <button
          className={cs('nav__burger', { 'nav__burger--open': isMobileMenuOpen })}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          <span className="nav__burger-icon">
            {isMobileMenuOpen ? (
              <img src="/close-menu.svg" alt="Close menu" width="32" height="32" />
            ) : (
              <img src="/burger-menu.svg" alt="Open menu" width="32" height="32" />
            )}
          </span>
        </button>
      </nav>
    );
  }

  return (
    <nav className={cs('nav', className)}>
      {/* Navigation menu */}
      <ul className={'nav__list'} ref={navListRef}>
        {navItems.map((item) => (
          <li className={cs('nav__list-item', { 'active-parent': item.isActive })} key={item.label}>
            {item.url ? (
              <Link
                className={cs('nav__link standalone', { active: item.isActive })}
                to={item.url.includes('.') && !item.url.startsWith('http') ? `https://${item.url}` : item.url}
                target={item.sameTab ? '_self' : '_blank'}
                onClick={handleLinkClick}
              >
                {item.label}
              </Link>
            ) : (
              item.label
            )}
            {item.children && item.children.length > 0 && (
              <ul>
                {item.children.map((child) => (
                  <li key={child.id}>
                    {child.url ? (
                      <Link
                        className={cs('nav__link standalone', { active: child.isActive })}
                        to={
                          child.url.includes('.') && !child.url.startsWith('http') ? `https://${child.url}` : child.url
                        }
                        target={item.sameTab ? '_self' : '_blank'}
                        onClick={handleLinkClick}
                      >
                        {child.label}
                      </Link>
                    ) : (
                      child.label
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default Nav;
